import { create } from 'zustand';
import { secureKeyApi } from '../features/auth/services/secureKeyService';
import type { AuthCredentials, SecureKeyStore } from '../types/secureKey';

/**
 * Zustand store for secure key management
 * Handles authentication, renewal, revocation, and expiration tracking
 */
export const useSecureKeyStore = create<SecureKeyStore>()((set, get) => ({
    // State
    secureKey: null,
    expiresAt: null,
    isExpiringSoon: false,
    isLoading: false,
    error: null,
    // Track when we last asked server for status to debounce requests
    lastCheckedAt: null,

    // Actions
    authenticate: async (credentials: AuthCredentials) => {
        set({ isLoading: true, error: null });

        try {
            const response = await secureKeyApi.authenticate(credentials);
            const expiresAt = new Date(response.expiresAt);

            set({
                secureKey: response.secureKey,
                expiresAt,
                isExpiringSoon: false,
                isLoading: false,
                error: null,
            });

            // Start expiration timer
            get().startExpirationTimer();
        } catch (error: any) {
            set({
                secureKey: null,
                expiresAt: null,
                isExpiringSoon: false,
                isLoading: false,
                error: error.message,
            });
            throw error;
        }
    },

    renew: async () => {
        const { secureKey } = get();
        if (!secureKey) {
            throw new Error('No secure key to renew');
        }

        set({ isLoading: true, error: null });

        try {
            const response = await secureKeyApi.renew(secureKey);
            const expiresAt = new Date(response.expiresAt);

            set({
                secureKey: response.secureKey,
                expiresAt,
                isExpiringSoon: false,
                isLoading: false,
                error: null,
            });

            // Restart expiration timer with new expiration
            get().startExpirationTimer();
        } catch (error: any) {
            set({
                isLoading: false,
                error: error.message,
            });
            throw error;
        }
    },

    revoke: async () => {
        const { secureKey } = get();
        if (!secureKey) {
            return; // Nothing to revoke
        }

        set({ isLoading: true });

        try {
            await secureKeyApi.revoke(secureKey);
        } catch (error) {
            // Don't throw errors for revoke - it's a cleanup operation
            console.warn('Failed to revoke secure key:', error);
        } finally {
            // Always clear the local state
            get().clearKey();
            set({ isLoading: false });
        }
    },

    // Add an optional `force` argument to bypass TTL/debounce
    checkStatus: async (force = false) => {
        const STATUS_TTL_MS = 30000; // 30 seconds default TTL

        const lastCheckedAt = (get() as any).lastCheckedAt as Date | null;
        if (!force && lastCheckedAt) {
            const msSince = Date.now() - lastCheckedAt.getTime();
            if (msSince < STATUS_TTL_MS) {
                // Recent check already performed — skip server call
                return;
            }
        }

        // Mark last checked now to debounce concurrent calls
        set({ lastCheckedAt: new Date() });

        const { secureKey } = get();

        try {
            const status = await secureKeyApi.getStatus(secureKey || undefined);

            if (status.hasActiveKey && status.expiresAt) {
                const expiresAt = new Date(status.expiresAt);
                set({
                    expiresAt,
                    isExpiringSoon: status.isExpiringSoon,
                });
            } else {
                // No active key on server
                get().clearKey();
            }
        } catch (error) {
            console.warn('Failed to check secure key status:', error);
        }
    },

    clearError: () => {
        set({ error: null });
    },

    clearKey: () => {
        get().stopExpirationTimer();
        set({
            secureKey: null,
            expiresAt: null,
            isExpiringSoon: false,
            error: null,
        });
    },

    startExpirationTimer: () => {
        // Be defensive: expiresAt may be a Date or a serialized string when rehydrated
        let expiresAtRaw = (get() as any).expiresAt;
        if (!expiresAtRaw) return;

        const expiresAt =
            expiresAtRaw instanceof Date
                ? expiresAtRaw
                : new Date(expiresAtRaw);

        // Clear any existing timer
        get().stopExpirationTimer();

        const checkExpiration = () => {
            const now = new Date();
            const timeLeft = expiresAt.getTime() - now.getTime();
            const minutesLeft = Math.floor(timeLeft / (1000 * 60));

            if (timeLeft <= 0) {
                // Key has expired
                get().clearKey();
                return;
            }

            if (minutesLeft <= 5) {
                // Key is expiring soon (5 minutes or less)
                set({ isExpiringSoon: true });
            }
        };

        // Check immediately
        checkExpiration();

        // Set up interval to check every 30 seconds
        // Store interval ID for cleanup (we'll use a closure for this)
        (get() as any)._expirationTimer = setInterval(checkExpiration, 30000);
    },

    stopExpirationTimer: () => {
        const timer = (get() as any)._expirationTimer;
        if (timer) {
            clearInterval(timer);
            delete (get() as any)._expirationTimer;
        }
    },
}));

// Helper hooks for common use cases
export const useSecureKey = () => {
    const secureKey = useSecureKeyStore((state) => state.secureKey);
    const isExpiringSoon = useSecureKeyStore((state) => state.isExpiringSoon);
    const hasActiveKey = Boolean(secureKey);

    return { secureKey, hasActiveKey, isExpiringSoon };
};

export const useSecureKeyActions = () => {
    const authenticate = useSecureKeyStore((state) => state.authenticate);
    const renew = useSecureKeyStore((state) => state.renew);
    const revoke = useSecureKeyStore((state) => state.revoke);
    const clearKey = useSecureKeyStore((state) => state.clearKey);
    const checkStatus = useSecureKeyStore((state) => state.checkStatus);

    return { authenticate, renew, revoke, clearKey, checkStatus };
};
